/**
 * PROVIDER TIER BADGES COMPONENT
 *
 * Visual indicator showing which provider tiers a model is available on.
 * Distinguishes between enabled (filled) and available but not enabled (outlined).
 */

import { VALID_PROVIDER_TIERS } from '../../../../shared-pricing-config'
import type { ProviderTier } from '@modelstack/shared-schema/auth'

interface ProviderTierBadgesProps {
  /** Tiers available for this model */
  availableTiers: Array<ProviderTier>
  /** Tiers enabled by the user */
  enabledTiers: Array<ProviderTier>
  /** Size variant */
  size?: 'sm' | 'md'
  /** Show check icon for enabled tiers */
  showCheckIcon?: boolean
  /** Compact mode (no labels, just colored dots) */
  compact?: boolean
}

const TIER_CONFIG: Record<
  ProviderTier,
  {
    label: string
    color: string
    bgColor: string
    borderColor: string
    icon?: string
  }
> = {
  economy: {
    label: 'Economy',
    color: 'text-[var(--color-info-text)]',
    bgColor: 'bg-[var(--color-info-bg)]',
    borderColor: 'border-[var(--color-info-border)]',
    icon: 'savings',
  },
  standard: {
    label: 'Standard',
    color: 'text-[var(--color-success-text)]',
    bgColor: 'bg-[var(--color-success-bg)]',
    borderColor: 'border-[var(--color-success-border)]',
    icon: 'verified',
  },
  premium: {
    label: 'Premium',
    color: 'text-[var(--color-primary)]',
    bgColor: 'bg-[var(--color-primary)]/10',
    borderColor: 'border-[var(--color-primary)]/30',
    icon: 'workspace_premium',
  },
}

export function ProviderTierBadges({
  availableTiers,
  enabledTiers,
  size = 'sm',
  showCheckIcon = false,
  compact = false,
}: ProviderTierBadgesProps) {
  if (availableTiers.length === 0) {
    return (
      <span className="text-xs text-[var(--color-text-tertiary)] border border-[var(--color-border)] rounded-full px-2.5 py-1 glass-panel">
        Not available
      </span>
    )
  }

  // Sort tiers: standard, premium. Drop any tier the API returns that this
  // UI doesn't know how to render (e.g. a stale/unrecognized value) instead
  // of crashing on an undefined TIER_CONFIG lookup.
  const tierOrder = [...VALID_PROVIDER_TIERS] as Array<ProviderTier>
  const sortedTiers = availableTiers
    .filter((tier) => tier in TIER_CONFIG)
    .sort((a, b) => tierOrder.indexOf(a) - tierOrder.indexOf(b))

  if (sortedTiers.length === 0) {
    return (
      <span className="text-xs text-[var(--color-text-tertiary)] border border-[var(--color-border)] rounded-full px-2.5 py-1 glass-panel">
        Not available
      </span>
    )
  }

  if (compact) {
    // Compact mode: colored dots only
    return (
      <div
        className="flex items-center gap-1"
        title={`Available on: ${sortedTiers.join(', ')}`}
      >
        {sortedTiers.map((tier) => {
          const config = TIER_CONFIG[tier]
          const isEnabled = enabledTiers.includes(tier)

          return (
            <div
              key={tier}
              className={`w-2 h-2 rounded-full border ${
                isEnabled
                  ? `${config.bgColor} ${config.borderColor}`
                  : `bg-transparent ${config.borderColor}`
              }`}
              title={`${config.label}${isEnabled ? ' (enabled)' : ' (available)'}`}
            />
          )
        })}
      </div>
    )
  }

  const badgeSize = size === 'sm' ? 'text-xs px-2.5 py-0.5' : 'text-sm px-3.5 py-1'

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {sortedTiers.map((tier) => {
        const config = TIER_CONFIG[tier]
        const isEnabled = enabledTiers.includes(tier)

        return (
          <div
            key={tier}
            className={`
              inline-flex items-center gap-1 rounded-full border font-medium transition-all duration-300 ease-brisk
              ${badgeSize}
              ${
                isEnabled
                  ? `${config.bgColor} ${config.borderColor} ${config.color}`
                  : `bg-transparent ${config.borderColor} ${config.color} opacity-60`
              }
            `}
            title={
              isEnabled
                ? `${config.label} (enabled)`
                : `${config.label} (available but not enabled)`
            }
          >
            {config.icon && (
              <span className="material-symbols-outlined !text-[14px]">
                {isEnabled && showCheckIcon ? 'check_circle' : config.icon}
              </span>
            )}
            <span>{config.label}</span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Single tier badge (for use in forms/selectors)
 */
interface TierBadgeProps {
  tier: ProviderTier
  enabled?: boolean
  onClick?: () => void
  size?: 'sm' | 'md'
}

export function TierBadge({
  tier,
  enabled = true,
  onClick,
  size = 'sm',
}: TierBadgeProps) {
  const config = TIER_CONFIG[tier]
  const badgeSize =
    size === 'sm'
      ? 'min-h-9 w-full px-2.5 py-1 text-xs'
      : 'min-h-11 w-full px-3 py-2 text-sm'
  const isClickable = !!onClick

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      aria-pressed={isClickable ? enabled : undefined}
      className={`
        inline-flex items-center gap-1.5 rounded-full border font-medium transition-all duration-300 ease-brisk
        ${badgeSize}
        ${
          enabled
            ? `${config.bgColor} ${config.borderColor} ${config.color}`
            : `bg-transparent ${config.borderColor} ${config.color} opacity-60`
        }
        ${isClickable ? 'cursor-pointer hover:opacity-80 active:scale-[0.97]' : 'cursor-default'}
      `}
    >
      {config.icon && (
        <span className="material-symbols-outlined !text-[14px]">
          {config.icon}
        </span>
      )}
      <span>{config.label}</span>
    </button>
  )
}

/**
 * Tier legend - removed (tooltips on badges provide sufficient guidance)
 */
export function TierLegend() {
  return null
}
